/**
 * Supporting Document Intelligence.
 *
 * Supporting Documents are EVIDENCE ONLY. They strengthen the narrative,
 * timeline, legal analysis and negotiation strategy. They are never the
 * canonical invoice.
 *
 * Hard invariants (enforced by supporting-documents.test.ts):
 *  - A supporting document is NEVER an invoice source file, so it can never
 *    enter Invoice OCR, change the invoice page count, add charge lines, or
 *    move subtotal / tax / total / storage days / dashboard / recovery values.
 *  - Invoice Intelligence remains the single financial source of truth.
 *  - Every extracted fact keeps its own lineage (document, page, type, OCR
 *    confidence, timestamp) and is never merged into invoice OCR lineage.
 *  - Conflicts with the invoice are preserved and flagged, never resolved by
 *    overwriting a canonical invoice value.
 *
 * Pipeline position:
 *   Submission → Invoice Upload → Invoice Intelligence →
 *   **Supporting Document Intelligence** → Vendor → Legal → Market →
 *   AI Findings → Professional Report
 */

/** Canonical `review_files.kind` for a supporting document. */
export const SUPPORTING_DOCUMENT_KIND = "supporting_document" as const;

/** Pre-rename value still present on existing claims. Read-compatible only. */
export const LEGACY_SUPPORTING_DOCUMENT_KINDS = ["attachment"] as const;

/** The only `kind` Invoice Intelligence may consume. */
export const INVOICE_KIND = "invoice" as const;

/** Source label a supporting document always carries. */
export const SUPPORTING_DOCUMENT_SOURCE = "supporting_document" as const;

export type SupportingDocumentType =
  | "police_report"
  | "tow_authorization"
  | "release_form"
  | "repair_estimate"
  | "shop_supplement"
  | "insurance_letter"
  | "email"
  | "photograph"
  | "court_document"
  | "storage_agreement"
  | "contract"
  | "receipt"
  | "inspection_report"
  | "other";

export type SupportingProcessingStatus = "pending" | "processing" | "completed" | "failed";

export interface FileLike {
  id?: string | null;
  kind?: string | null;
  file_name?: string | null;
  mime_type?: string | null;
  bucket?: string | null;
  storage_path?: string | null;
  file_size?: number | null;
  created_at?: string | null;
  uploaded_by?: string | null;
}

/* ───────────────────────── classification ───────────────────────── */

const TYPE_LABELS: Record<SupportingDocumentType, string> = {
  police_report: "Police Report",
  tow_authorization: "Tow Authorization",
  release_form: "Release Form",
  repair_estimate: "Repair Estimate",
  shop_supplement: "Shop Supplement",
  insurance_letter: "Insurance Letter",
  email: "Email",
  photograph: "Photograph",
  court_document: "Court Document",
  storage_agreement: "Storage Agreement",
  contract: "Contract",
  receipt: "Receipt",
  inspection_report: "Inspection Report",
  other: "Supporting Document",
};

export function supportingDocumentTypeLabel(t: SupportingDocumentType): string {
  return TYPE_LABELS[t] ?? TYPE_LABELS.other;
}

/**
 * Evidence fields each document type may contribute. Purely descriptive —
 * none of these are financial inputs.
 */
export const SUPPORTING_EVIDENCE_FIELDS: Record<SupportingDocumentType, string[]> = {
  police_report: ["incident_date", "tow_reason", "officer_information", "impound_information"],
  tow_authorization: ["authorization_status", "authorizing_party", "authorization_date"],
  release_form: ["release_available", "release_date", "signatures"],
  repair_estimate: ["repair_facility", "estimate_amount", "repair_timeline"],
  shop_supplement: ["repair_facility", "supplement_scope", "repair_timeline"],
  insurance_letter: ["insurer", "coverage_position", "communication_date"],
  email: ["communication_timeline", "promises", "delays", "authorization_discussion"],
  photograph: ["vehicle_condition", "lot_condition", "signage", "documentation_quality"],
  court_document: ["case_reference", "filing_date", "order_summary"],
  storage_agreement: ["agreement_date", "agreed_terms", "signatures"],
  contract: ["parties", "agreement_date", "agreed_terms"],
  receipt: ["payment_date", "payer", "payment_reference"],
  inspection_report: ["inspection_date", "inspector", "condition_findings"],
  other: ["document_summary"],
};

const PATTERNS: Array<[SupportingDocumentType, RegExp]> = [
  [
    "police_report",
    /\b(police|sheriff|law[\s_-]?enforcement|crash|accident|incident)[\s_-]*(report|record)?\b|\bpd[\s_-]?report\b/i,
  ],
  ["tow_authorization", /\b(tow|towing)[\s_-]*(auth|authorization|authorisation|consent|order)\b/i],
  ["release_form", /\b(release|releasing)[\s_-]*(form|authorization|letter|notice)?\b/i],
  ["shop_supplement", /\bsupplement\b/i],
  ["repair_estimate", /\b(repair|body[\s_-]?shop)?[\s_-]*estimate\b|\bappraisal\b/i],
  ["insurance_letter", /\b(insurance|insurer|adjuster|carrier|claim)[\s_-]*(letter|denial|coverage|correspondence)\b/i],
  ["email", /\b(e-?mail|correspondence|message[\s_-]?thread|inbox)\b/i],
  ["court_document", /\b(court|docket|subpoena|petition|judgment|order|filing|case[\s_-]?no)\b/i],
  ["storage_agreement", /\bstorage[\s_-]*(agreement|contract|terms)\b/i],
  ["contract", /\b(contract|agreement|terms[\s_-]?of[\s_-]?service|tos)\b/i],
  ["inspection_report", /\binspection\b/i],
  ["receipt", /\b(receipt|payment[\s_-]?confirmation|paid[\s_-]?invoice)\b/i],
];

export interface Classification {
  documentType: SupportingDocumentType;
  /** 0-1. Filename/MIME heuristic only — never presented as verified fact. */
  classificationConfidence: number;
}

/**
 * Deterministic, offline classification from filename + MIME type. Unknown
 * documents are `other` with low confidence: we never guess a type we cannot
 * support, and we never classify a supporting document as an invoice page.
 */
export function classifySupportingDocument(
  fileName: string | null | undefined,
  mimeType?: string | null,
): Classification {
  const name = (fileName ?? "").toLowerCase();
  const mime = (mimeType ?? "").toLowerCase();

  if (mime.startsWith("image/") || /\.(png|jpe?g|webp|gif|heic|heif|tiff?)$/i.test(name)) {
    // A photo may still be a scanned report; a textual signal wins.
    for (const [type, re] of PATTERNS) {
      if (re.test(name)) return { documentType: type, classificationConfidence: 0.7 };
    }
    return { documentType: "photograph", classificationConfidence: 0.8 };
  }
  if (mime.includes("message/rfc822") || /\.(eml|msg)$/i.test(name)) {
    return { documentType: "email", classificationConfidence: 0.9 };
  }
  for (const [type, re] of PATTERNS) {
    if (re.test(name)) return { documentType: type, classificationConfidence: 0.65 };
  }
  return { documentType: "other", classificationConfidence: 0.2 };
}

/* ───────────────────────── file partitioning ───────────────────────── */

function kindOf(f: FileLike): string {
  return (f.kind ?? "").toLowerCase();
}

/** True when the row is a supporting document (canonical or legacy kind). */
export function isSupportingDocument(f: FileLike): boolean {
  const k = kindOf(f);
  return (
    k === SUPPORTING_DOCUMENT_KIND ||
    (LEGACY_SUPPORTING_DOCUMENT_KINDS as readonly string[]).includes(k)
  );
}

/**
 * Files Invoice Intelligence (OCR, page count, charge extraction) may read.
 *
 * A supporting document is excluded unconditionally. Untagged legacy rows stay
 * eligible ONLY when the claim has no explicitly tagged invoice file, which
 * preserves pre-tagging claims without ever pulling a supporting document in.
 */
export function selectInvoiceSourceFiles<T extends FileLike>(files: T[] | null | undefined): T[] {
  if (!files?.length) return [];
  const notSupporting = files.filter((f) => !isSupportingDocument(f));
  const tagged = notSupporting.filter((f) => kindOf(f) === INVOICE_KIND);
  if (tagged.length) return tagged;
  // Legacy claims: everything that is not a supporting document / report.
  return notSupporting.filter((f) => kindOf(f) !== "report");
}

/** Files Supporting Document Intelligence may read. Never invoice files. */
export function selectSupportingDocuments<T extends FileLike>(
  files: T[] | null | undefined,
): T[] {
  if (!files?.length) return [];
  return files.filter(isSupportingDocument);
}

/* ───────────────────────── document records ───────────────────────── */

export interface SupportingDocumentRecord {
  document_id: string | null;
  review_id: string | null;
  owner_id: string | null;
  document_type: SupportingDocumentType;
  document_type_label: string;
  mime_type: string | null;
  original_filename: string | null;
  storage_path: string | null;
  bucket: string | null;
  file_size: number | null;
  uploaded_at: string | null;
  classification_confidence: number;
  processing_status: SupportingProcessingStatus;
  source: typeof SUPPORTING_DOCUMENT_SOURCE;
}

export function buildSupportingDocumentRecord(
  file: FileLike,
  ctx: { reviewId?: string | null; ownerId?: string | null } = {},
): SupportingDocumentRecord {
  const { documentType, classificationConfidence } = classifySupportingDocument(
    file.file_name,
    file.mime_type,
  );
  return {
    document_id: file.id ?? null,
    review_id: ctx.reviewId ?? null,
    owner_id: ctx.ownerId ?? file.uploaded_by ?? null,
    document_type: documentType,
    document_type_label: supportingDocumentTypeLabel(documentType),
    mime_type: file.mime_type ?? null,
    original_filename: file.file_name ?? null,
    storage_path: file.storage_path ?? null,
    bucket: file.bucket ?? null,
    file_size: file.file_size ?? null,
    uploaded_at: file.created_at ?? null,
    classification_confidence: classificationConfidence,
    processing_status: "pending",
    source: SUPPORTING_DOCUMENT_SOURCE,
  };
}

export function buildSupportingDocumentRecords(
  files: FileLike[] | null | undefined,
  ctx: { reviewId?: string | null; ownerId?: string | null } = {},
): SupportingDocumentRecord[] {
  return selectSupportingDocuments(files ?? []).map((f) =>
    buildSupportingDocumentRecord(f, ctx),
  );
}

/* ───────────────────────── evidence lineage ───────────────────────── */

/**
 * A single fact extracted from a supporting document. Lineage is mandatory and
 * lives in its own namespace — it is never written into invoice OCR lineage.
 */
export interface SupportingEvidenceFact {
  documentId: string | null;
  documentType: SupportingDocumentType;
  page: number | null;
  field: string;
  value: string;
  ocrConfidence: number | null;
  extractedAt: string;
  source: typeof SUPPORTING_DOCUMENT_SOURCE;
}

export function buildSupportingEvidenceFact(input: {
  record: SupportingDocumentRecord;
  field: string;
  value: string;
  page?: number | null;
  ocrConfidence?: number | null;
  extractedAt?: string;
}): SupportingEvidenceFact {
  return {
    documentId: input.record.document_id,
    documentType: input.record.document_type,
    page: input.page ?? null,
    field: input.field,
    value: input.value,
    ocrConfidence: input.ocrConfidence ?? null,
    extractedAt: input.extractedAt ?? new Date().toISOString(),
    source: SUPPORTING_DOCUMENT_SOURCE,
  };
}

/* ───────────────────────── conflict handling ───────────────────────── */

export interface SupportingConflict {
  field: string;
  invoiceValue: string;
  supportingValue: string;
  documentId: string | null;
  documentType: SupportingDocumentType;
  /** Both values are preserved; the invoice always stays authoritative. */
  authoritative: "invoice";
}

function comparable(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * Compare supporting-document facts against canonical invoice values. Never
 * mutates anything: it only reports discrepancies for the AI to explain.
 */
export function detectSupportingConflicts(
  facts: SupportingEvidenceFact[],
  invoiceValues: Record<string, string | null | undefined>,
): SupportingConflict[] {
  const out: SupportingConflict[] = [];
  for (const f of facts) {
    const invoiceValue = invoiceValues[f.field];
    if (!invoiceValue || !f.value) continue;
    if (comparable(invoiceValue) === comparable(f.value)) continue;
    out.push({
      field: f.field,
      invoiceValue,
      supportingValue: f.value,
      documentId: f.documentId,
      documentType: f.documentType,
      authoritative: "invoice",
    });
  }
  return out;
}

/* ───────────────────────── AI prompt block ───────────────────────── */

/**
 * Prompt block handed to AI Findings / Legal / Negotiation. It carries the
 * document inventory and evidence facts, and states the guardrails explicitly:
 * evidence may strengthen a finding, never generate recovery dollars, invoice
 * adjustments, market adjustments or vendor identity.
 */
export function supportingDocumentsPromptBlock(
  records: SupportingDocumentRecord[],
  facts: SupportingEvidenceFact[] = [],
  conflicts: SupportingConflict[] = [],
): string | null {
  if (!records.length) return null;
  const inventory = records.map((r) => ({
    document_id: r.document_id,
    document_type: r.document_type,
    filename: r.original_filename,
    uploaded_at: r.uploaded_at,
    classification_confidence: r.classification_confidence,
    source: r.source,
  }));
  const lines = [
    "SUPPORTING_DOCUMENTS (source: supporting_document — evidence only, NOT the invoice):",
    JSON.stringify(inventory),
  ];
  if (facts.length) {
    lines.push("", "SUPPORTING_DOCUMENT_FACTS (each fact carries its own lineage):", JSON.stringify(facts));
  }
  if (conflicts.length) {
    lines.push(
      "",
      "SUPPORTING_DOCUMENT_CONFLICTS (preserve BOTH values and explain the discrepancy; the invoice value stays authoritative):",
      JSON.stringify(conflicts),
    );
  }
  lines.push(
    "",
    "Rules for SUPPORTING_DOCUMENTS:",
    "- They are supplemental evidence. Invoice Intelligence is the only financial source of truth.",
    "- They must NEVER create invoice charge lines, change the invoice page count, subtotal, tax, total or storage days.",
    "- They must NEVER independently create recovery dollars, market adjustments, benchmark changes or vendor identity.",
    "- They MAY strengthen the executive summary, case brief, timeline, legal analysis, negotiation strategy, recommendations, confidence, evidence strength and missing-document detection.",
    "- Attribute every statement to its origin: invoice, supporting document, customer context, verified external source, or AI analysis.",
    "- Distinguish verified evidence from customer statements and from invoice facts.",
  );
  return lines.join("\n");
}
